import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography,
    SelectChangeEvent,
} from '@mui/material';
import {
    KeyboardArrowLeft,
    KeyboardArrowRight,
    FirstPage,
    LastPage,
} from '@mui/icons-material';

interface SCUIGenericPaginationProps {
    count: number; // Toplam kayıt sayısı
    page: number; // Mevcut sayfa (1-indexed)
    rowsPerPage: number; // Sayfa başına kayıt sayısı
    onPageChange: (page: number) => void; // Sayfa değişim callback'i
    onRowsPerPageChange?: (rowsPerPage: number) => void; // Sayfa boyutu değişim callback'i (opsiyonel)
    rowsPerPageOptions?: number[]; // Sayfa boyutu seçenekleri (opsiyonel, örn: [10, 20, 50])
    showPageInput?: boolean; // Doğrudan sayfa numarası girme alanını göster/gizle
    disabled?: boolean; // Tüm kontrolleri devre dışı bırak
}

export const SCUIGenericPagination: FC<SCUIGenericPaginationProps> = ({
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions = [10, 20, 50],
    showPageInput = true,
    disabled = false,
}) => {
    const { t } = useTranslation();
    const totalPages = Math.max(Math.ceil(count / rowsPerPage), 1);
    const [pageInput, setPageInput] = useState<string>(String(page));

    // Dışarıdan gelen sayfa değiştiğinde input değerini güncelle
    useEffect(() => {
        setPageInput(String(page));
    }, [page]);

    const handlePageClick = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
            onPageChange(newPage);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        // Sadece sayısal değerlere izin ver
        if (val === '' || /^\d+$/.test(val)) {
            setPageInput(val);
        }
    };

    const handleInputSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const parsedPage = Number.parseInt(pageInput, 10);
        if (!Number.isNaN(parsedPage) && parsedPage >= 1 && parsedPage <= totalPages) {
            onPageChange(parsedPage);
        } else {
            // Geçersiz giriş durumunda mevcut sayfaya geri dön
            setPageInput(String(page));
        }
    };

    const handleInputBlur = () => {
        const parsedPage = Number.parseInt(pageInput, 10);
        if (!Number.isNaN(parsedPage) && parsedPage >= 1 && parsedPage <= totalPages) {
            onPageChange(parsedPage);
        } else {
            setPageInput(String(page));
        }
    };

    const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
        if (onRowsPerPageChange) {
            onRowsPerPageChange(Number(event.target.value));
        }
    };

    // Sayfa numaralarını oluşturma algoritması (Ellipsis ile)
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const siblingCount = 1; // Aktif sayfanın sağında ve solunda gösterilecek sayfa sayısı

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        const leftSiblingIndex = Math.max(page - siblingCount, 1);
        const rightSiblingIndex = Math.min(page + siblingCount, totalPages);

        const shouldShowLeftDots = leftSiblingIndex > 2;
        const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

        pages.push(1);

        if (shouldShowLeftDots) {
            pages.push('left-dots');
        }

        for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
            if (i !== 1 && i !== totalPages) {
                pages.push(i);
            }
        }

        if (shouldShowRightDots) {
            pages.push('right-dots');
        }

        pages.push(totalPages);

        return pages;
    };

    const startItem = (page - 1) * rowsPerPage + 1;
    const endItem = Math.min(page * rowsPerPage, count);

    return (
        <Box
            className="sc-generic-pagination"
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                width: '100%',
                py: 2,
                px: 3,
                backgroundColor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            {/* Sol Taraf: Bilgi Metni */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {count > 0 ? (
                        <>
                            <strong>{startItem}-{endItem}</strong> / <strong>{count}</strong> {t('general.records', 'kayıt gösteriliyor')}
                        </>
                    ) : (
                        t('general.noRecords', 'Kayıt bulunamadı')
                    )}
                </Typography>
            </Box>

            {/* Orta Taraf: Sayfalama Butonları */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                }}
            >
                <IconButton
                    size="small"
                    onClick={() => handlePageClick(1)}
                    disabled={page === 1 || disabled}
                    color="primary"
                >
                    <FirstPage />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={() => handlePageClick(page - 1)}
                    disabled={page === 1 || disabled}
                    color="primary"
                >
                    <KeyboardArrowLeft />
                </IconButton>

                {getPageNumbers().map((pageNum) => {
                    if (pageNum === 'left-dots' || pageNum === 'right-dots') {
                        return (
                            <Typography
                                key={pageNum}
                                variant="body2"
                                sx={{ px: 1, color: 'text.secondary' }}
                            >
                                ...
                            </Typography>
                        );
                    }

                    const isCurrent = pageNum === page;
                    return (
                        <Button
                            key={`page-${pageNum}`}
                            size="small"
                            onClick={() => handlePageClick(Number(pageNum))}
                            disabled={disabled}
                            variant={isCurrent ? 'contained' : 'outlined'}
                            color={isCurrent ? 'primary' : 'inherit'}
                            sx={{
                                minWidth: 32,
                                height: 32,
                                p: 0,
                                borderRadius: '50%',
                                fontWeight: isCurrent ? 'bold' : 'normal',
                                border: isCurrent ? 'none' : '1px solid',
                                borderColor: 'divider',
                                '&:hover': {
                                    backgroundColor: isCurrent ? 'primary.main' : 'action.hover',
                                },
                            }}
                        >
                            {pageNum}
                        </Button>
                    );
                })}

                <IconButton
                    size="small"
                    onClick={() => handlePageClick(page + 1)}
                    disabled={page === totalPages || disabled}
                    color="primary"
                >
                    <KeyboardArrowRight />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={() => handlePageClick(totalPages)}
                    disabled={page === totalPages || disabled}
                    color="primary"
                >
                    <LastPage />
                </IconButton>
            </Box>

            {/* Sağ Taraf: Sayfa Boyutu ve Doğrudan Giriş */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    flexDirection: { xs: 'column', sm: 'row' },
                    width: { xs: '100%', md: 'auto' },
                    justifyContent: 'flex-end',
                }}
            >
                {/* Sayfa Boyutu Seçimi */}
                {onRowsPerPageChange && rowsPerPageOptions.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('general.rowsPerPage', 'Sayfa başına:')}
                        </Typography>
                        <Select
                            size="small"
                            value={rowsPerPage}
                            onChange={handleRowsPerPageChange}
                            disabled={disabled}
                            sx={{
                                height: 32,
                                fontSize: '0.875rem',
                                borderRadius: 1.5,
                                '.MuiSelect-select': { py: 0.5, px: 1.5 },
                            }}
                        >
                            {rowsPerPageOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                )}

                {/* Doğrudan Sayfa Girişi */}
                {showPageInput && totalPages > 1 && (
                    <Box
                        component="form"
                        onSubmit={handleInputSubmit}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {t('general.goToPage', 'Git:')}
                        </Typography>
                        <TextField
                            size="small"
                            value={pageInput}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                            disabled={disabled}
                            slotProps={{
                                htmlInput: {
                                    style: {
                                        textAlign: 'center',
                                        fontSize: '0.8125rem',
                                    },
                                },
                            }}
                            sx={{
                                width: '45px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    height: '26px',
                                    padding: 0,
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '2px 4px !important',
                                },
                            }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            / {totalPages}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
